import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button, Badge } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
import outputs from '../../../amplify_outputs.json';
import { useUserContext } from '../Context/UserContext';

type Issue = Schema['Issue']['type'];

type Proposal = {
  id: string;
  title: string;
  summary: string;
  steps: string[];
  benefits: string[];
  risksOrTradeoffs: string[];
  successMetric: string;
};

const INFER_INTERESTS_API =
  (outputs as any)?.custom?.inferInterestsApiUrl
    ? String((outputs as any).custom.inferInterestsApiUrl).replace(/\/$/, '')
    : '';

const GOOGLE_GREEN = '#34A853';
const btnGreenStyle: React.CSSProperties = { backgroundColor: GOOGLE_GREEN, borderColor: GOOGLE_GREEN };
const linkGreenStyle: React.CSSProperties = { color: GOOGLE_GREEN, textDecoration: 'none' };

/* ----------------------------- helpers ----------------------------- */

function coerceStrings(arr?: (string | null)[] | null): string[] {
  return (arr ?? []).filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
}

/** Parse AWSJSON (handles double-encoded strings) */
function parseAwsJson<T = any>(v: unknown): T | null {
  let cur: any = v;
  for (let i = 0; i < 3; i++) {
    if (cur == null) return null;
    if (typeof cur === 'string') {
      try { cur = JSON.parse(cur); continue; } catch { return null; }
    }
    if (typeof cur === 'object') return cur as T;
  }
  return null;
}

const arrEq = (a?: string[] | null, b?: string[] | null) =>
  !!a && !!b && a.length === b.length && a.every((v, i) => v === b[i]);

/** Accept many shapes for proposedSolutionsJson */
function proposalsFromProposedSolutionsJson(raw: unknown): Proposal[] | null {
  const obj = parseAwsJson<any>(raw);

  const arrDirect = Array.isArray(obj) ? obj : null;

  const arrSolutions =
    Array.isArray(obj?.solutions) ? obj.solutions :
    Array.isArray(obj?.proposedSolutions) ? obj.proposedSolutions :
    Array.isArray(obj?.options) ? obj.options :
    Array.isArray(obj?.data?.solutions) ? obj.data.solutions :
    Array.isArray(obj?.payload?.solutions) ? obj.payload.solutions :
    null;

  const arr = arrDirect || arrSolutions;
  if (!Array.isArray(arr)) return null;

  const mapped: Proposal[] = arr.map((p: any, idx: number) => ({
    id: String(p?.id ?? `sol-${idx + 1}`),
    title: String(p?.title ?? p?.name ?? `Option ${idx + 1}`).trim(),
    summary: String(p?.summary ?? p?.rationale ?? p?.description ?? '').trim(),
    steps: Array.isArray(p?.steps) ? p.steps.map(String) : [],
    benefits: Array.isArray(p?.benefits) ? p.benefits.map(String) : [],
    risksOrTradeoffs: Array.isArray(p?.risksOrTradeoffs ?? p?.risks) ? (p.risksOrTradeoffs ?? p.risks).map(String) : [],
    successMetric: String(p?.successMetric ?? p?.metric ?? '').trim(),
  }));

  const anyContent = mapped.some(
    (p) => p.title || p.summary || p.benefits.length || p.risksOrTradeoffs.length || p.successMetric
  );
  return anyContent ? mapped : null;
}

/** Accept worker’s direct `solutions` array if it ever exists */
function proposalsFromDirectSolutions(raw: unknown): Proposal[] | null {
  const arr = Array.isArray(raw) ? raw : null;
  if (!arr) return null;
  const mapped: Proposal[] = arr.map((s: any, idx: number) => ({
    id: String(s?.id ?? `sol-${idx + 1}`),
    title: String(s?.title ?? `Option ${idx + 1}`).trim(),
    summary: String(s?.rationale ?? s?.summary ?? '').trim(),
    steps: Array.isArray(s?.steps) ? s?.steps.map(String) : [],
    benefits: Array.isArray(s?.benefits) ? s?.benefits.map(String) : [],
    risksOrTradeoffs: Array.isArray(s?.risksOrTradeoffs ?? s?.risks) ? (s?.risksOrTradeoffs ?? s?.risks).map(String) : [],
    successMetric: String(s?.successMetric ?? '').trim(),
  }));
  return mapped.length ? mapped : null;
}

/** Support normalized `sessionSolutions` if present */
function proposalsFromSessionSolutions(raw: unknown): Proposal[] | null {
  if (!Array.isArray(raw)) return null;
  const mapped: Proposal[] = raw.map((p: any, idx: number) => ({
    id: String(p?.id ?? `sol-${idx + 1}`),
    title: String(p?.title ?? `Option ${idx + 1}`).trim(),
    summary: String(p?.summary ?? '').trim(),
    steps: Array.isArray(p?.steps) ? p.steps.map(String) : [],
    benefits: Array.isArray(p?.benefits) ? p.benefits.map(String) : [],
    risksOrTradeoffs: Array.isArray(p?.risksOrTradeoffs) ? p.risksOrTradeoffs.map(String) : [],
    successMetric: String(p?.successMetric ?? '').trim(),
  }));
  return mapped.length ? mapped : null;
}

/** Single-source extractor that tolerates all shapes */
function extractProposals(issue: any): Proposal[] | null {
  const fromJson = proposalsFromProposedSolutionsJson(issue?.proposedSolutionsJson);
  if (fromJson && fromJson.length) return ensureFiveWithStableIds(fromJson);

  const fromDirect = proposalsFromDirectSolutions((issue as any)?.solutions);
  if (fromDirect && fromDirect.length) return ensureFiveWithStableIds(fromDirect);

  const fromSession = proposalsFromSessionSolutions((issue as any)?.sessionSolutions);
  if (fromSession && fromSession.length) return ensureFiveWithStableIds(fromSession);

  return null;
}

function ensureFiveWithStableIds(list: Proposal[]): Proposal[] {
  const copy = [...list].slice(0, 5).map((p, i) => ({ ...p, id: p.id || `sol-${i + 1}` }));
  while (copy.length < 5) {
    const idx = copy.length;
    copy.push({
      id: `sol-${idx + 1}`,
      title: `Option ${idx + 1}`,
      summary: '',
      steps: [],
      benefits: [],
      risksOrTradeoffs: [],
      successMetric: '',
    });
  }
  return copy;
}

function orderToScores(order: string[]): Record<string, number> {
  const scores: Record<string, number> = {};
  order.forEach((id, idx) => { scores[id] = idx + 1; });
  return scores;
}

function reorder<T>(list: T[], startIndex: number, endIndex: number) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

function pickBestBySum(rows: Array<{ id: string; a: number; b: number; sum: number }>): string {
  const sorted = [...rows].sort((x, y) => {
    if (x.sum !== y.sum) return x.sum - y.sum;
    const dx = Math.abs(x.a - x.b), dy = Math.abs(y.a - y.b);
    if (dx !== dy) return dx - dy;
    const maxX = Math.max(x.a, x.b), maxY = Math.max(y.a, y.b);
    if (maxX !== maxY) return maxX - maxY;
    return x.id.localeCompare(y.id);
  });
  return sorted[0].id;
}

function pickPrecedenceHuman(it: any): string | undefined {
  const flat = typeof it?.precedencePrincipleHuman === 'string' ? it.precedencePrincipleHuman.trim() : '';
  if (flat) return flat;
  const nested = typeof it?.precedencePrinciple?.human === 'string' ? it.precedencePrinciple.human.trim() : '';
  return nested || undefined;
}

function findRawChosen(issue: any, chosen: { id: string; title?: string }) {
  const chosenId = String(chosen?.id ?? '').trim();
  const chosenTitle = String(chosen?.title ?? '').trim();

  const pj = parseAwsJson<any>(issue?.proposedSolutionsJson);
  if (pj && Array.isArray(pj.solutions)) {
    const hit =
      pj.solutions.find((s: any) => String(s?.id) === chosenId) ??
      pj.solutions.find((s: any) => String(s?.title ?? '').trim() === chosenTitle);
    if (hit) return hit;
  }

  const arrays = [issue?.sessionSolutions, issue?.solutions].filter(Array.isArray) as any[][];
  for (const arr of arrays) {
    const hit =
      arr.find((s: any) => String(s?.id) === chosenId) ??
      arr.find((s: any) => String(s?.title ?? '').trim() === chosenTitle);
    if (hit) return hit;
  }
  return null;
}

/* ----------------------------- component ----------------------------- */

export default function SessionSolutionsPage() {
  const { id } = useParams<{ id?: string }>();
  const issueId = useMemo(() => (id ? decodeURIComponent(id).trim() : ''), [id]);
  const navigate = useNavigate();
  const { email: userEmail } = useUserContext();

  const [issue, setIssue] = useState<Issue | null>(null);
  const [proposals, setProposals] = useState<Proposal[] | null>(null);
  const [precedencePrincipleHuman, setPrecedencePrincipleHuman] = useState<string | undefined>(undefined);

  const [loading, setLoading] = useState(true);
  const [waitingForAI, setWaitingForAI] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [initiatorOrder, setInitiatorOrder] = useState<string[] | null>(null);
  const [collaboratorOrder, setCollaboratorOrder] = useState<string[] | null>(null);
  const [top3Ids, setTop3Ids] = useState<string[] | null>(null);

  const mounted = useRef(true);
  const enqueuedRef = useRef(false);
  const loggedDbOnceRef = useRef(false);
  const autoPromotedRef = useRef(false);
  const autoTop3TriggeredRef = useRef(false);

  const pollTimerRef = useRef<number | null>(null);
  const refreshTimerRef = useRef<number | null>(null);

  type Phase = 'five' | 'top3';
  const phase: Phase = top3Ids && top3Ids.length === 3 ? 'top3' : 'five';
  type Role = 'initiator' | 'collaborator';

  const isInitiator = useMemo(() => {
    if (!issue || !userEmail) return false;
    return (issue.initiatorEmail ?? '').trim().toLowerCase() === (userEmail ?? '').trim().toLowerCase();
  }, [issue, userEmail]);
  const myRole: Role = isInitiator ? 'initiator' : 'collaborator';

  const makeSaveKey = (role: Role, ph: Phase) => `solutionsSaved:${issueId}:${role}:${ph}`;

  const [saveLockedFive, setSaveLockedFive] = useState(false);
  const [saveLockedTop3, setSaveLockedTop3] = useState(false);

  function readPhaseLocks(role: Role) {
    try {
      const f = typeof window !== 'undefined' ? window.localStorage.getItem(makeSaveKey(role, 'five')) : null;
      const t = typeof window !== 'undefined' ? window.localStorage.getItem(makeSaveKey(role, 'top3')) : null;
      setSaveLockedFive(f === 'true');
      setSaveLockedTop3(t === 'true');
    } catch {}
  }

  function unsubscribe(sub: any) {
    try { sub?.unsubscribe?.(); } catch {}
    try { typeof sub === 'function' && sub(); } catch {}
  }

  function stopPolling() {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
      console.debug('[SessionSolutions][poll] stopped');
    }
  }

  function stopRefreshTimer() {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
      console.debug('[SessionSolutions][refresh] canceled');
    }
  }

  /** Minimal GraphQL for precedence */
  const GET_PRECEDENCE = /* GraphQL */ `
  query GetIssuePrecedence($id: ID!) {
    getIssue(id: $id) {
      id
      precedencePrincipleHuman
      precedencePrinciple
    }
  }
`;

  /** Safe GET without non-existent fields */
  const GET_SOLUTIONS = /* GraphQL */ `
  query GetIssueSolutions($id: ID!) {
    getIssue(id: $id) {
      id
      status
      initiatorEmail
      collaboratorEmail
      initiatorFirstName
      initiatorLastName
      collaboratorFirstName
      collaboratorLastName
      initiatorSolutionOrder
      collaboratorSolutionOrder
      topThreeSolutionIds
      proposedSolutionsJson
      precedencePrincipleHuman
      precedencePrinciple
      agreedIssueMeta
      bestSolutionId
      bestSolutionJson
      updatedAt
      agreedIssue
    }
  }
`;

  async function fetchPrecedenceHuman(id: string): Promise<string | undefined> {
    try {
      const res = await (client as any).graphql({ query: GET_PRECEDENCE, variables: { id }, authMode: 'userPool' });
      const it = (res as any)?.data?.getIssue;
      return pickPrecedenceHuman(it);
    } catch {
      return undefined;
    }
  }

  async function readIssueOnce(): Promise<Issue | null> {
    try {
      const res = await (client as any).graphql({
        query: GET_SOLUTIONS,
        variables: { id: issueId },
        authMode: 'userPool',
      });
      const data = (res as any)?.data?.getIssue ?? null;

      const human = await fetchPrecedenceHuman(issueId);
      if (human) (data as any).precedencePrincipleHuman = human;

      if (!loggedDbOnceRef.current) {
        console.log('[SessionSolutions] Issue DB entry:', data);
        loggedDbOnceRef.current = true;
      }

      if (mounted.current) setIssue(data);
      return data as Issue | null;
    } catch (e: any) {
      const msg =
        e?.errors?.[0]?.message ||
        e?.message ||
        'GraphQL error';
      console.error('[SessionSolutions] readIssueOnce failed:', e);
      throw new Error(msg);
    }
  }

  async function callEnqueue() {
    if (!INFER_INTERESTS_API) throw new Error('InferInterests API URL is not configured.');
    if (enqueuedRef.current) {
      console.debug('[SessionSolutions] enqueue skipped');
      return;
    }
    enqueuedRef.current = true;

    const url = `${INFER_INTERESTS_API}/infer-interests/enqueue`;
    console.debug('[SessionSolutions] Enqueue URL:', url);

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json;charset=UTF-8' },
      body: JSON.stringify({ issueId, persist: true }),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`InferInterests enqueue failed: ${res.status} ${t || '(no body)'}`);
    }
    console.debug('[SessionSolutions] Enqueued');
  }

  function hasText(a?: (string | null)[] | null) {
    return coerceStrings(a as any).length > 0;
  }
  function readyForResolving(it: Issue) {
    const iIssue = hasText(it.initiatorIssue as any);
    const cIssue = hasText(it.collaboratorIssue as any);
    const cDesired = hasText((it as any).desiredOutcomeCollaborator);
    const ok = iIssue && cIssue && cDesired;
    if (!ok) {
      console.debug('[SessionSolutions] Not ready resolving_issue', { iIssue, cIssue, cDesired, status: (it as any)?.status });
    }
    return ok;
  }
  async function promoteToResolving(id: string) {
    if (autoPromotedRef.current) return;
    autoPromotedRef.current = true;
    console.debug('[SessionSolutions] Promoting to resolving_issue');
    await client.models.Issue.update(
      { id, status: 'resolving_issue', lastActivityAt: new Date().toISOString() } as any,
      { authMode: 'userPool' }
    );
  }

  /** observeQuery: DO NOT refetch here; use subscription payload directly */
  function subscribeIssue(issueId: string) {
    const anyClient: any = client as any;

    const selection = [
      'id',
      'status',
      'initiatorEmail',
      'collaboratorEmail',
      'initiatorFirstName',
      'initiatorLastName',
      'collaboratorFirstName',
      'collaboratorLastName',
      'initiatorSolutionOrder',
      'collaboratorSolutionOrder',
      'topThreeSolutionIds',
      'proposedSolutionsJson',
      'precedencePrincipleHuman',
      'precedencePrinciple',
      'agreedIssueMeta',
      'bestSolutionId',
      'bestSolutionJson',
      'updatedAt',
      'agreedIssue',
    ];

    const onNewData = (row: any) => {
      if (!row) return;

      setIssue(row);

      const human =
        (typeof row?.precedencePrincipleHuman === 'string' && row.precedencePrincipleHuman.trim()) ||
        (typeof row?.precedencePrinciple?.human === 'string' && row.precedencePrinciple.human.trim()) ||
        undefined;
      if (human) setPrecedencePrincipleHuman(human);

      const initOrder = (row as any)?.initiatorSolutionOrder ?? null;
      const collOrder = (row as any)?.collaboratorSolutionOrder ?? null;
      const t3       = (row as any)?.topThreeSolutionIds ?? null;

      if (Array.isArray(initOrder)) setInitiatorOrder(prev => (arrEq(prev, initOrder) ? prev : initOrder));
      if (Array.isArray(collOrder)) setCollaboratorOrder(prev => (arrEq(prev, collOrder) ? prev : collOrder));
      if (Array.isArray(t3)) setTop3Ids(prev => (arrEq(prev, t3) ? prev : t3));

      const p = extractProposals(row);

      if (p && p.length) {
        setProposals([...p]);
        setWaitingForAI(false);
        setLoading(false);
        stopPolling();
        stopRefreshTimer();
        return;
      }

      if (row?.status !== 'resolving_issue' && readyForResolving(row)) {
        promoteToResolving(issueId).catch(() => {});
        setWaitingForAI(true);
        callEnqueue().catch(() => {});
        return;
      }
      if (row?.status === 'resolving_issue') {
        setWaitingForAI(true);
        callEnqueue().catch(() => {});
      }
    };

    if (anyClient?.models?.Issue?.observeQuery) {
      const obs = anyClient.models.Issue.observeQuery({
        filter: { id: { eq: issueId } },
        selection,
        authMode: 'userPool',
      });

      const sub = obs.subscribe({
        next: (payload: any) => {
          const items = payload?.items ?? [];
          const first = Array.isArray(items) ? items[0] : null;
          if (first) onNewData(first);
        },
        error: (err: any) => {
          console.error('[SessionSolutions] observeQuery error:', err);
        },
      });

      return () => unsubscribe(sub);
    }

    return () => {};
  }

  async function init() {
    setError(null);
    enqueuedRef.current = false;
    autoPromotedRef.current = false;

    const it = await readIssueOnce();
    if (!it) throw new Error('Session not found.');

    const existingHuman = pickPrecedenceHuman(it as any);
    if (existingHuman) setPrecedencePrincipleHuman(existingHuman);

    const initOrder = (it as any)?.initiatorSolutionOrder ?? null;
    const collInitOrder = (it as any)?.collaboratorSolutionOrder ?? null;
    const t3 = (it as any)?.topThreeSolutionIds ?? null;

    if (Array.isArray(initOrder)) setInitiatorOrder(initOrder);
    if (Array.isArray(collInitOrder)) setCollaboratorOrder(collInitOrder);
    if (Array.isArray(t3)) setTop3Ids(t3);

    const existing = extractProposals(it as any);
    if (existing && existing.length) {
      setProposals([...existing]);
      setWaitingForAI(false);
      setLoading(false);
      stopPolling();
      stopRefreshTimer();
    } else {
      if (it.status !== 'resolving_issue' && readyForResolving(it)) {
        await promoteToResolving(issueId).catch(() => {});
        await callEnqueue().catch(() => {});
        setWaitingForAI(true);
      } else if (it.status === 'resolving_issue') {
        await callEnqueue().catch(() => {});
        setWaitingForAI(true);
      } else {
        setWaitingForAI(false);
      }
    }

    readPhaseLocks(myRole);
    return subscribeIssue(issueId);
  }

  useEffect(() => {
    mounted.current = true;
    let cleanup: (() => void) | undefined;

    (async () => {
      try {
        if (!issueId) throw new Error('Missing session id.');
        cleanup = await init();
      } catch (e: any) {
        const msg = e?.message || 'Failed to prepare solutions.';
        setError(msg);
        setLoading(false);
      }
    })();

    return () => {
      mounted.current = false;
      cleanup?.();
      stopPolling();
      stopRefreshTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId]);

  useEffect(() => {
    if (!issueId) return;
    readPhaseLocks(myRole);
  }, [issueId, myRole, phase]);

  /* ---------- polling fallback until proposals appear ---------- */
  useEffect(() => {
    if (!issueId) return;
    if (proposals?.length) { stopPolling(); return; }

    if (pollTimerRef.current) return;
    pollTimerRef.current = window.setInterval(async () => {
      try {
        const res = await client.models.Issue.get({ id: issueId }, { authMode: 'userPool' }) as any;
        const row = (res as any)?.data ?? res ?? null;
        if (!row) return;

        const p = extractProposals(row);
        if (p?.length) {
          setIssue(row);
          setProposals([...p]);
          setWaitingForAI(false);
          setLoading(false);
          stopPolling();
          stopRefreshTimer();
        } else if (row?.status === 'resolving_issue') {
          setWaitingForAI(true);
        }
      } catch (err) {
        console.warn('[SessionSolutions][poll] error:', err);
      }
    }, 3000) as unknown as number;

    return stopPolling;
  }, [issueId, proposals?.length]);

  /* ---------- 30s backup ---------- */
  useEffect(() => {
    if (!issueId) return;
    if (proposals?.length) { stopRefreshTimer(); return; }
    if (refreshTimerRef.current) return;

    refreshTimerRef.current = window.setTimeout(async () => {
      try {
        const it = await readIssueOnce();
        const p = extractProposals(it as any);
        if (p?.length) {
          setProposals([...p]);
          setWaitingForAI(false);
          setLoading(false);
          stopPolling();
        }
      } catch {
      } finally {
        stopRefreshTimer();
      }
    }, 30000) as unknown as number;

    return stopRefreshTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId, proposals?.length]);

  /* ----------------------------- ranking & scoring ----------------------------- */

  const [myOrder, setMyOrder] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!proposals?.length) return;
    if (isDirty) return;

    const fiveIds = proposals.map((p) => p.id);
    const useInit = Array.isArray(initiatorOrder) && initiatorOrder.length > 0 ? initiatorOrder : null;
    const useColl = Array.isArray(collaboratorOrder) && collaboratorOrder.length > 0 ? collaboratorOrder : null;

    let next = fiveIds;

    if (top3Ids && top3Ids.length === 3) {
      const mine = myRole === 'initiator' ? useInit : useColl;
      const inter = (mine ?? fiveIds).filter((id) => top3Ids.includes(id));
      next = inter.length === 3 ? inter : top3Ids;
    } else {
      next = (myRole === 'initiator' && useInit?.length === 5)
        ? useInit
        : (myRole === 'collaborator' && useColl?.length === 5)
          ? useColl
          : fiveIds;
    }

    if (!arrEq(myOrder, next)) setMyOrder(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals, initiatorOrder, collaboratorOrder, top3Ids, myRole, isDirty]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const currentTop3Phase = top3Ids && top3Ids.length === 3;

    const visibleIdsNow = currentTop3Phase
      ? myOrder.filter((id) => top3Ids!.includes(id))
      : myOrder;

    const reordered = reorder(visibleIdsNow, result.source.index, result.destination.index);

    if (currentTop3Phase) {
      const others = myOrder.filter((id) => !top3Ids!.includes(id));
      setMyOrder([...reordered, ...others]);
    } else {
      setMyOrder(reordered);
    }
    setIsDirty(true);
  };

  const saveMyOrder = async () => {
    if (!issue || !proposals?.length) return;

    try {
      const inTop3Phase = !!top3Ids && top3Ids.length === 3;
      const orderToPersist = inTop3Phase ? myOrder.filter((id) => top3Ids!.includes(id)) : myOrder;
      const scores = orderToScores(orderToPersist);

      const byId = new Map(proposals.map(p => [p.id, p]));
      const rankingsArray = orderToPersist.map((solId) => ({
        sol: solId,
        title: byId.get(solId)?.title ?? '',
        value: scores[solId],
      }));

      const patch: Partial<Issue> & { id: string } = { id: issue!.id };

      if (myRole === 'initiator') {
        (patch as any).initiatorSolutionOrder = orderToPersist;
        (patch as any).initiatorSolutionRankings = rankingsArray;
      } else {
        (patch as any).collaboratorSolutionOrder = orderToPersist;
        (patch as any).collaboratorSolutionRankings = rankingsArray;
      }

      const res = await client.models.Issue.update(patch, { authMode: 'userPool' });
      if (!res?.data) throw new Error('Failed to save order');

      if (myRole === 'initiator') setInitiatorOrder(orderToPersist);
      else setCollaboratorOrder(orderToPersist);

      setIsDirty(false);

      try {
        const key = `solutionsSaved:${issueId}:${myRole}:${inTop3Phase ? 'top3' : 'five'}`;
        window.localStorage.setItem(key, 'true');
        if (inTop3Phase) setSaveLockedTop3(true);
        else setSaveLockedFive(true);
      } catch {}
    } catch (e) {
      console.error('Save order failed', e);
      setError('Failed to save your order. Please try again.');
    }
  };

  const scoreRows = useMemo(() => {
    if (!proposals?.length) return [];
    const ids = proposals.map((p) => p.id);
    const aScores = Array.isArray(initiatorOrder) && initiatorOrder.length >= 3 ? orderToScores(initiatorOrder) : null;
    const bScores = Array.isArray(collaboratorOrder) && collaboratorOrder.length >= 3 ? orderToScores(collaboratorOrder) : null;

    return ids.map((id) => {
      const s = proposals.find((x) => x.id === id)!;
      const a = aScores ? aScores[id] ?? null : null;
      const b = bScores ? bScores[id] ?? null : null;
      const sum = a != null && b != null ? a + b : null;
      return { id, title: s.title, a, b, sum };
    });
  }, [proposals, initiatorOrder, collaboratorOrder]);

  const bothHaveFive =
    (initiatorOrder?.length === 5) &&
    (collaboratorOrder?.length === 5);

  const canComputeTop3 = !!proposals?.length && bothHaveFive;

  async function persistTop3(
    top3: string[],
    rows: Array<{ id: string; title: string; a: number; b: number; sum: number }>
  ) {
    const snapshot = rows.map((r) => ({
      sol: r.id,
      title: r.title,
      initiatorScore: r.a ?? null,
      collaboratorScore: r.b ?? null,
      sum: r.sum ?? null,
    }));

    try {
      const res = await client.models.Issue.update({
        id: issue!.id,
        topThreeSolutionIds: top3,
        solutionScores: snapshot,
        status: (issue as any).status !== 'resolving_issue' ? 'resolving_issue' : (issue as any).status,
      } as any, { authMode: 'userPool' });

      if (!res?.data) throw new Error('Failed to save top 3');

      setTop3Ids(top3);
      const myInter = myOrder.filter((id) => top3.includes(id));
      setMyOrder(myInter.length === 3 ? myInter : top3);
    } catch (e) {
      console.error('Saving top3 failed', e);
      setError('Failed to save the Top 3 solutions.');
    }
  }

  const computeTop3 = async () => {
    if (!issue || !canComputeTop3) return;

    const rows = scoreRows.filter(
      (r) => r.sum != null && r.a != null && r.b != null
    ) as Array<{ id: string; title: string; a: number; b: number; sum: number }>;

    if (rows.length !== 5) {
      setError('Both parties must rank all five solutions before computing the Top 3.');
      return;
    }

    const bySum = [...rows].sort((x, y) => x.sum - y.sum);

    const thirdSum = bySum[2].sum;
    const strictlyBetter = bySum.filter((r) => r.sum < thirdSum);
    const need = Math.max(0, 3 - strictlyBetter.length);

    if (need === 0) {
      await persistTop3(bySum.slice(0, 3).map((r) => r.id), rows);
      return;
    }

    const tiedAtThird = bySum.filter((r) => r.sum === thirdSum);

    const bySmallestDisagreement = [...tiedAtThird].sort((x, y) => {
      const dx = Math.abs(x.a - x.b);
      const dy = Math.abs(y.a - y.b);
      if (dx !== dy) return dx - dy;
      const maxX = Math.max(x.a, x.b);
      const maxY = Math.max(y.a, y.b);
      if (maxX !== maxY) return maxX - maxY;
      return x.id.localeCompare(y.id);
    });

    const chosenFromTies = bySmallestDisagreement.slice(0, need);
    const finalTop3 = [...strictlyBetter, ...chosenFromTies].slice(0, 3).map((r) => r.id);

    await persistTop3(finalTop3, rows);
  };

  /* Auto-compute Top 3 once when both have ranked five and no Top 3 yet */
  useEffect(() => {
    if (!canComputeTop3) return;
    if (top3Ids && top3Ids.length === 3) return;
    if (autoTop3TriggeredRef.current) return;
    autoTop3TriggeredRef.current = true;
    computeTop3().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canComputeTop3, top3Ids]);

  /* ----------------------------- FINAL SELECTION ----------------------------- */

  const finalPickId = useMemo(() => {
    if (!top3Ids || top3Ids.length !== 3) return null;
    if (!initiatorOrder || initiatorOrder.length !== 3) return null;
    if (!collaboratorOrder || collaboratorOrder.length !== 3) return null;

    const aScores = orderToScores(initiatorOrder);
    const bScores = orderToScores(collaboratorOrder);

    const agreed = top3Ids.find(id => aScores[id] === 1 && bScores[id] === 1);
    return agreed ?? null;
  }, [top3Ids, initiatorOrder, collaboratorOrder]);

  const isFinalized = !!finalPickId;

  const bestSolutionId = useMemo(() => {
    if (!top3Ids || top3Ids.length !== 3) return null;
    if (!initiatorOrder || initiatorOrder.length !== 3) return null;
    if (!collaboratorOrder || collaboratorOrder.length !== 3) return null;
    if (isFinalized) return null;

    const aScores = orderToScores(initiatorOrder);
    const bScores = orderToScores(collaboratorOrder);

    const rows: Array<{ id: string; a: number; b: number; sum: number }> = top3Ids.map((id) => ({
      id,
      a: aScores[id],
      b: bScores[id],
      sum: aScores[id] + bScores[id],
    }));

    if (rows.some(r => r.a == null || r.b == null)) return null;

    return pickBestBySum(rows);
  }, [top3Ids, initiatorOrder, collaboratorOrder, isFinalized]);

  const byId = useMemo(() => new Map((proposals ?? []).map(p => [p.id, p])), [proposals]);
  const finalizedSolution = useMemo(() => (isFinalized && byId.get(finalPickId!)) || null, [isFinalized, finalPickId, byId]);
  const computedBestSolution = useMemo(() => (bestSolutionId && byId.get(bestSolutionId)) || null, [bestSolutionId, byId]);

  const savedBestRef = useRef(false);
  useEffect(() => {
    if (!issueId) return;

    const bothTop3Saved =
      Array.isArray(initiatorOrder) && initiatorOrder.length === 3 &&
      Array.isArray(collaboratorOrder) && collaboratorOrder.length === 3;

    if (!bothTop3Saved) return;
    if (!top3Ids || top3Ids.length !== 3) return;

    const chosen = finalizedSolution ?? computedBestSolution;
    if (!chosen) return;

    const alreadyInDb =
      !!(issue as any)?.bestSolutionId ||
      !!(issue as any)?.bestSolutionJson;

    if (savedBestRef.current || alreadyInDb) return;

    (async () => {
      try {
        const normalized = {
          id: String(chosen.id).trim(),
          title: String(chosen.title || '').trim(),
          summary: String(chosen.summary || '').trim(),
          steps: Array.isArray(chosen.steps) ? chosen.steps : [],
          benefits: Array.isArray(chosen.benefits) ? chosen.benefits : [],
          risksOrTradeoffs: Array.isArray(chosen.risksOrTradeoffs) ? chosen.risksOrTradeoffs : [],
          successMetric: String(chosen.successMetric || '').trim(),
        };

        const raw = findRawChosen(issue, chosen);

        const bestSolutionJson = JSON.stringify({
          version: 1,
          savedAt: new Date().toISOString(),
          issueId,
          chosenId: normalized.id,
          normalized,
          raw: raw ?? null,
          proposals: proposals ?? [],
          context: {
            topThreeSolutionIds: top3Ids ?? null,
            precedencePrincipleHuman: precedencePrincipleHuman ?? null,
            initiatorDCS: (issue as any)?.initiatorDCS ?? null,
            collaboratorDCS: (issue as any)?.collaboratorDCS ?? null,
          },
        });

        const bestId = normalized.id;
        const bestTitle =
          (normalized.title || normalized.summary || `Option ${bestId.replace(/^sol-?/, '')}`).trim();

        const existingMeta = (() => {
          try {
            const m = (issue as any)?.agreedIssueMeta;
            if (!m) return {};
            return typeof m === 'string' ? JSON.parse(m) : (m as Record<string, any>);
          } catch {
            return {};
          }
        })();
        const agreedIssueMetaUpdated = JSON.stringify({
          ...existingMeta,
          bestSolution: normalized,
        });

        const res = await client.models.Issue.update(
          {
            id: issueId,
            bestSolutionId: bestId,
            bestSolution: bestTitle,
            bestSolutionJson,
            agreedIssueMeta: agreedIssueMetaUpdated,
            status: 'resolved',
            lastActivityAt: new Date().toISOString(),
          } as any,
          { authMode: 'userPool' }
        );

        if (!res?.data) throw new Error('Failed to persist bestSolution');

        savedBestRef.current = true;
        navigate(`/session/${encodeURIComponent(issueId)}/resolve`, { replace: true });
      } catch (e) {
        console.warn('[SessionSolutions] Failed to persist bestSolution]:', e);
      }
    })();
  }, [
    issueId,
    top3Ids,
    initiatorOrder,
    collaboratorOrder,
    finalizedSolution,
    computedBestSolution,
    proposals,
    precedencePrincipleHuman,
    issue,
    navigate,
  ]);

  /* ----------------------------- UI derivations ----------------------------- */

  const agreedIssueText = useMemo(() => {
    const direct = (issue as any)?.agreedIssue;
    if (typeof direct === 'string' && direct.trim()) return direct.trim();
    try {
      const meta = (issue as any)?.agreedIssueMeta;
      const parsed = typeof meta === 'string' ? JSON.parse(meta) : meta;
      const candidate =
        parsed?.agreedIssue ||
        parsed?.agreedIssueText ||
        parsed?.agreedStatement ||
        '';
      return (typeof candidate === 'string' && candidate.trim()) ? candidate.trim() : '';
    } catch {
      return '';
    }
  }, [issue]);

  const visibleIds = useMemo(() => {
    if (finalizedSolution) return [finalizedSolution.id];
    if (computedBestSolution) return [computedBestSolution.id];
    if (top3Ids && top3Ids.length === 3) {
      const inter = myOrder.filter((id) => top3Ids.includes(id));
      return inter.length === 3 ? inter : top3Ids;
    }
    return myOrder;
  }, [myOrder, top3Ids, finalizedSolution, computedBestSolution]);

  const visibleProposals = useMemo(() => {
    if (!proposals) return [];
    const map = new Map(proposals.map((p) => [p.id, p]));
    return visibleIds.map((id) => map.get(id)!).filter(Boolean);
  }, [proposals, visibleIds]);

  const alreadyHaveFive = !!(proposals && proposals.length >= 5);

  /* ----------------------------- render ----------------------------- */

  const initiatorName = (i: Issue | null) =>
    i ? `${i.initiatorFirstName} ${i.initiatorLastName}`.trim() : '';
  const collaboratorName = (i: Issue | null) =>
    i ? `${i.collaboratorFirstName} ${i.collaboratorLastName}`.trim() : '';

  if (!issueId) {
    return (
      <Container fluid="md" className="min-vh-100 d-flex align-items-start justify-content-center py-4">
        <Alert variant="warning" className="mt-5">Missing session id.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container fluid="md" className="min-vh-100 d-flex align-items-start justify-content-center py-4">
        <div className="text-center mt-5">
          <Spinner animation="border" role="status" />
          <div className="mt-3">Preparing solution options…</div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid="md" className="min-vh-100 d-flex align-items-start justify-content-center py-4">
        <Card className="mx-auto" style={{ maxWidth: 1200, width: '100%' }}>
          <Card.Body>
            <Alert variant="danger" className="mb-3">{error}</Alert>
            <div className="d-flex justify-content-end gap-2">
              <Button onClick={() => window.location.reload()}>Reload</Button>
              <Button variant="link" onClick={() => navigate('/session')} style={linkGreenStyle}>My Sessions</Button>
              <Button variant="link" onClick={() => navigate('/')} style={linkGreenStyle}>Home</Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const nameForHeader = isInitiator
    ? (issue?.initiatorFirstName ?? '')
    : (issue?.collaboratorFirstName ?? '');

  const isSavedFive =
    phase === 'five' &&
    (
      (myRole === 'initiator' && arrEq(myOrder, initiatorOrder)) ||
      (myRole === 'collaborator' && arrEq(myOrder, collaboratorOrder))
    );

  const saveDisabled =
    (phase === 'five' && (saveLockedFive || isSavedFive)) ||
    (phase === 'top3' && saveLockedTop3);

  const dragDisabled =
    (phase === 'five' && saveLockedFive) ||
    (phase === 'top3' && saveLockedTop3) ||
    !!finalizedSolution ||
    !!computedBestSolution;

  const CARD_WIDTH = 210;
  const GAP_PX = 12;

  const justifyForCount = (n: number) => {
    if (n === 1 || n === 3) return 'center';
    if (n >= 5) return 'space-between';
    return 'flex-start';
  };

  return (
    <Container fluid="md" className="min-vh-100 d-flex align-items-start justify-content-center py-4">
      <Card className="mx-auto" style={{ maxWidth: 1200, width: '100%' }}>
        {/* agreed issue banner */}
        {agreedIssueText ? (
          <div
  className="mx-3 mt-3"
  style={{
    background: '#E7F1FF',
    border: '1px solid #CFE2FF',
    borderRadius: 12,
    padding: '12px 16px',
  }}
>
  <strong>You both agreed upon:</strong> {agreedIssueText}
</div>

        ) : null}

        <Card.Body>
          {precedencePrincipleHuman && (
            <Alert variant="info" className="py-2">
              <strong>Dfuse thinks: </strong>
              <span>{precedencePrincipleHuman}</span>
            </Alert>
          )}

          <div className="mb-3 text-muted">
            For: <strong>{initiatorName(issue)}</strong> & <strong>{collaboratorName(issue)}</strong>
            {waitingForAI && !proposals && <span className="ms-2">(generating…)</span>}
          </div>

          {/* moved header line below "For:" */}
          <h5 className="mb-3">
            {`${(nameForHeader || '').trim()}, drag the boxes in order of preference. Left is your highest ranking solution and right is the lowest ranking solution.`}
          </h5>

          {!alreadyHaveFive && (
            <div className="mb-3 d-flex justify-content-end">
              <Button
                onClick={() => { enqueuedRef.current = false; setProposals(null); callEnqueue().catch(() => {}); setWaitingForAI(true); }}
                disabled={waitingForAI || (issue?.status === 'resolving_issue')}
                title={issue?.status === 'resolving_issue' ? 'Solutions are already being generated for this session' : undefined}
              >
                {waitingForAI || issue?.status === 'resolving_issue' ? 'Generating…' : 'Generate 5 Solutions'}
              </Button>
            </div>
          )}

          {!proposals && waitingForAI && (
            <div className="text-center my-4">
              <Spinner animation="border" role="status" />
              <div className="mt-2">Generating practical options…</div>
            </div>
          )}

          {proposals && visibleIds.length > 0 && (
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  {finalizedSolution
                    ? 'Chosen Solution'
                    : (top3Ids
                        ? 'Top 3 Solutions (drag left → right)'
                        : 'After ranking the solutions in order of preference, save your choices to submit them.')}
                </div>
                <div className="d-flex gap-2">
                  {!finalizedSolution && !computedBestSolution && (
                    <Button
                      size="lg"
                      onClick={saveMyOrder}
                      disabled={saveDisabled}
                      title={saveDisabled ? 'Your ranking is saved/locked' : 'Save your rankings'}
                      style={btnGreenStyle}
                      className="text-white"
                    >
                      {phase === 'five'
                        ? (saveDisabled ? 'Saved' : 'Save Rankings')
                        : (saveDisabled ? 'Saved' : 'Save Top-3 Ranking')}
                    </Button>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="solutions-droppable" direction="horizontal">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="d-flex flex-row flex-nowrap"
                        style={{
                          justifyContent: justifyForCount(visibleProposals.length),
                          gap: `${GAP_PX}px`,
                          minHeight: 180,
                          overflowX: 'visible',
                          width: '100%',
                          paddingBottom: 6,
                        }}
                      >
                        {visibleProposals.map((p, index) => (
                          <Draggable
                            key={p.id}
                            draggableId={p.id}
                            index={index}
                            isDragDisabled={dragDisabled}
                          >
                            {(drag) => (
                              <Card
                                ref={drag.innerRef}
                                {...drag.draggableProps}
                                {...drag.dragHandleProps}
                                style={{
                                  ...drag.draggableProps.style,
                                  width: CARD_WIDTH,
                                  minWidth: CARD_WIDTH,
                                  maxWidth: CARD_WIDTH,
                                  cursor: dragDisabled ? 'default' : 'grab',
                                  opacity: dragDisabled ? 0.9 : 1,
                                  alignSelf: 'stretch',
                                }}
                              >
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Badge bg="dark">{index + 1}</Badge>
                                  </div>
                                  <Card.Title className="mb-2">{p.title}</Card.Title>
                                  {p.summary && <Card.Text className="mb-3">{p.summary}</Card.Text>}

                                  {!!p.steps?.length && (
                                    <>
                                      <h6 className="mb-1">Steps</h6>
                                      <ol className="mb-3">
                                        {p.steps.map((s, i) => <li key={i}>{s}</li>)}
                                      </ol>
                                    </>
                                  )}

                                  {!!p.benefits?.length && (
                                    <>
                                      <h6 className="mb-1">Benefits</h6>
                                      <ul className="mb-3">
                                        {p.benefits.map((b, i) => <li key={i}>{b}</li>)}
                                      </ul>
                                    </>
                                  )}

                                  {!!p.risksOrTradeoffs?.length && (
                                    <>
                                      <h6 className="mb-1">Risks / Tradeoffs</h6>
                                      <ul className="mb-3">
                                        {p.risksOrTradeoffs.map((r, i) => <li key={i}>{r}</li>)}
                                      </ul>
                                    </>
                                  )}

                                  {p.successMetric && (
                                    <div className="small text-muted">
                                      <strong>Success metric:</strong> {p.successMetric}
                                    </div>
                                  )}
                                </Card.Body>
                              </Card>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </Card.Body>
            </Card>
          )}

          <div className="d-flex justify-content-end gap-3 mt-4">
            <Button variant="link" onClick={() => navigate('/session')} style={linkGreenStyle}>
              My Sessions
            </Button>
            <Button variant="link" onClick={() => navigate('/')} style={linkGreenStyle}>
              Home
            </Button>
          </div>

        </Card.Body>
      </Card>
    </Container>
  );
}
